
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { ProductCard } from '@/components/product/card';
import { Loader2 } from 'lucide-react';

interface RelatedPurchasesProps {
  title: string;
  listingId?: number; // Optional: to show related purchases for a specific product
  limit?: number;
  buttonLink?: string;
  buttonText?: string;
}

const RelatedPurchases = ({
  title,
  listingId,
  limit = 4,
  buttonLink,
  buttonText
}: RelatedPurchasesProps) => {
  const { data: listings, isLoading, error } = useQuery({
    queryKey: ['relatedPurchases', listingId, limit],
    queryFn: async () => {
      let relatedIds: number[] = [];
      
      if (listingId) {
        // If we have a specific listing ID, get products related to it
        const { data: relatedData, error: relatedError } = await supabase
          .rpc('get_related_purchases', { 
            p_listing_id: listingId, 
            limit_count: limit 
          });
          
        if (relatedError) throw relatedError;
        
        relatedIds = relatedData?.map(item => item.related_id) || [];
      } else {
        // Otherwise, get the most commonly related products (most frequent pairs)
        const { data: topRelated, error: topRelatedError } = await supabase
          .from('related_purchases')
          .select('related_listing_id, relation_count')
          .order('relation_count', { ascending: false })
          .limit(limit);
          
        if (topRelatedError) throw topRelatedError;
        
        relatedIds = topRelated?.map(item => item.related_listing_id) || [];
      }
      
      if (relatedIds.length === 0) {
        return [];
      }
      
      // Fetch full listing details for the related IDs
      const { data: listingsData, error: listingsError } = await supabase
        .from('listings')
        .select(`
          *,
          categories(*)
        `)
        .in('id', relatedIds)
        .eq('is_active', true)
        .eq('approval_status', 'approved')
        .eq('is_deleted', false);
      
      if (listingsError) throw listingsError;
      
      // Sort the listings in the same order as the related IDs
      const sortedListings = relatedIds.map(id => 
        listingsData?.find(listing => listing.id === id)
      ).filter(Boolean);
      
      return sortedListings || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !listingId || !!listingId, // Enable if no listingId OR if listingId exists
  });

  if (error) {
    console.error("Error fetching related purchases:", error);
    return null; // Don't show section on error
  }

  if (isLoading) {
    return (
      <div className="py-12">
        <h2 className="text-3xl font-bold mb-4">{title}</h2>
        <div className="flex justify-center items-center py-12">
          <Loader2 className="animate-spin h-8 w-8 text-gray-400" />
        </div>
      </div>
    );
  }
  
  // If no related listings are available, don't show section
  if (!listings || listings.length === 0) {
    return null;
  }

  return (
    <div className="py-12">
      <div className="flex justify-between items-center mb-6 pb-2 border-b border-gray-100">
        <h2 className="text-3xl font-bold">{title}</h2>
        {buttonLink && (
          <Link to={buttonLink}>
            <Button variant="default" className="bg-brand-500 hover:bg-brand-600">{buttonText || 'Shiko më shumë'}</Button>
          </Link>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {listings.map((listing) => (
          <ProductCard
            key={listing.id}
            id={listing.id.toString()}
            title={listing.title}
            price={listing.price}
            location={listing.location}
            imageUrl={listing.images?.[0] || ''}
            category={listing.categories?.name || ''}
            isPremium={listing.is_premium}
            createdAt={listing.created_at}
            condition={listing.condition}
            deliveryOption={listing.delivery_option || 'shipping_and_pickup'}
            user_id={listing.user_id}
            stock={listing.stock}
            is_price_negotiable={listing.is_price_negotiable}
            make={listing.make}
            model={listing.model}
            year={listing.year}
            mileage={listing.mileage}
            transmission={listing.transmission}
          />
        ))}
      </div>
    </div>
  );
};

export default RelatedPurchases;
